import * as THREE from 'three';
import { clamp } from '@/core/math/scalar';
import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { DeviceCapabilities } from './Capabilities';
import { GalaxyBackground } from './GalaxyBackground';
import { QualitySettings } from './QualityProfiles';
import { ScreenSpaceReflection } from './ScreenSpaceReflection';

/**
 * Depth partition for one render pass.
 *
 * The near/far ratio of each partition is kept small enough that a 24-bit depth
 * buffer resolves it cleanly. This is the alternative to the naive approach of
 * one camera with `near = 0.0001, far = 1e10`, which has a ratio of 1e14 and
 * produces a depth buffer where everything beyond a few hundred metres occupies
 * the same handful of depth values.
 */
export interface DepthPartition {
  name: string;
  near: number;
  far: number;
}

export type PassName = 'deepSpace' | 'astronomical' | 'worldFar' | 'worldMid' | 'worldNear' | 'firstPerson';

/**
 * Layered renderer.
 *
 * Six passes, rendered back to front, with the depth buffer cleared between
 * each. Every camera sits at the render-frame origin and shares one
 * orientation, so the passes composite into a single coherent view.
 *
 *   deepSpace     starfield, no depth interaction
 *   astronomical  other bodies at compressed distances
 *   worldFar      the active body from far away          40 km .. 200,000 km
 *   worldMid      regional terrain                        300 m .. 60 km
 *   worldNear     local terrain, vessels, props           0.25 m .. 400 m
 *   firstPerson   cockpit and held equipment              0.02 m .. 60 m
 *
 * The three world passes render *the same scene* with three different cameras
 * rather than partitioning objects between scenes. That removes an entire class
 * of bug: an object never has to be assigned to a partition, so an object
 * straddling a boundary cannot fall through the gap. Frustum culling means each
 * pass only draws what lies within its own depth range, and the deliberate
 * overlap between ranges means anything spanning a boundary is drawn by both
 * passes, with the nearer one winning.
 */
/**
 * Render layers.
 *
 * The world scene is drawn three times, once per depth partition, and normally
 * that is exactly right: frustum culling decides what each pass draws, so an
 * object cannot end up in the wrong one. It is wrong for a full-sky shell. The
 * atmosphere surrounds the camera, so it intersects every partition's frustum
 * and gets drawn three times — and it is the most expensive shader in the
 * project, a ray march that covers the whole screen whenever the camera looks
 * up. Standing on a planet looking at the sky, that alone tripled the frame
 * cost.
 *
 * Sky shells therefore go on their own layer, enabled on exactly one camera.
 * They belong in the far pass: nearer terrain is drawn afterwards and correctly
 * paints over them, and aerial perspective on that terrain is computed in the
 * terrain shader rather than by the shell.
 */
export const RENDER_LAYER = {
  /** Everything that is not a sky shell. */
  world: 0,
  /** Atmosphere and analytic sky shells; drawn only by the far world pass. */
  sky: 2,
  /**
   * Cloud decks, drawn by all three world passes.
   *
   * Not the far pass alone: from the ground the deck is a few kilometres
   * overhead, well inside the far camera's near plane, so it would be clipped
   * away exactly where the player is standing under it.
   *
   * And not mid-and-far either, which is what it was. The mid partition starts
   * a few hundred metres out on the ground, so a deck anywhere nearer than that
   * — which is where it is whenever the viewer is flying through it, or below
   * it on a world whose deck sits low — was clipped by the mid camera's near
   * plane with no pass left to draw it. That is the "clouds cut off near the
   * camera" artefact: a hole in the deck centred on the viewer, its radius set
   * by the near plane rather than by anything in the sky.
   *
   * The camera frusta still overlap, but CloudLayer flattens its raster-only
   * proxy into every clip volume and assigns each fragment from the real slab
   * entry depth. `updateCloudPassRanges` therefore retains the geometric
   * overlap without blending a cloud twice or exposing a seam between passes.
   */
  clouds: 3,
  /**
   * Terrain chunk meshes, in addition to their default layer. The self-shadow
   * cascade cameras enable only this layer, so the depth-only pass they
   * render sees terrain and nothing else — no ocean, clouds, atmosphere
   * shell or vessel — without needing a separate scene graph for it.
   */
  terrainShadowCaster: 4,
} as const;

/** One cascade of the terrain self-shadow map. */
export interface ShadowCascade {
  camera: THREE.OrthographicCamera;
  target: THREE.WebGLRenderTarget;
  /** Half-width of this cascade's frustum, metres. */
  splitDistance: number;
  /** `projectionMatrix * matrixWorldInverse`, refreshed once per frame. */
  matrix: THREE.Matrix4;
}

/** Cascade count never exceeds this; `QualityProfiles` tops out at 4. */
const MAX_CASCADES = 4;

/**
 * Each cascade's half-width as a fraction of the quality profile's
 * `shadowDistance`. The near cascade covers roughly the `worldNear` depth
 * partition; the last covers the profile's full shadow distance.
 */
const CASCADE_SPLIT_FRACTIONS = [0.05, 0.2, 0.5, 1.0];

/**
 * Default depth-partition bounds, metres — see the class doc comment's table
 * for the ranges these are meant to cover. `worldFar`/`worldMid`/`worldNear`
 * are recomputed every frame by `adaptDepthRanges` (see its own constants
 * below); their defaults here only matter until the first `adaptDepthRanges`
 * call, which happens before the first frame renders.
 */
const DEEP_SPACE_NEAR_M = 1;
const DEEP_SPACE_FAR_M = 200;
/** Smallest/largest compressed render-distance `AstronomicalLayer` can produce (see its `MIN_RENDER_DISTANCE`/`COMPRESSION_K`). */
const ASTRONOMICAL_NEAR_M = 0.5;
const ASTRONOMICAL_FAR_M = 40_000;
const WORLD_FAR_NEAR_M = 40_000;
const WORLD_FAR_FAR_M = 2.4e8;
const WORLD_MID_NEAR_M = 300;
const WORLD_MID_FAR_M = 60_000;
const WORLD_NEAR_NEAR_M = 0.25;
const WORLD_NEAR_FAR_M = 400;
/** Cockpit/held-equipment scale; never adapted, unlike the three world partitions. */
const FIRST_PERSON_NEAR_M = 0.02;
const FIRST_PERSON_FAR_M = 60;

/**
 * `adaptDepthRanges` altitude breakpoints, metres above the surface. Below
 * `LOW` the vessel is on or just off the ground and the near partition needs
 * centimetre-scale precision; between `LOW` and `HIGH` it's airborne but
 * still close; above `HIGH` it can relax further, buying back depth-buffer
 * precision for terrain that's actually visible from up there.
 */
const NEAR_ALTITUDE_LOW_M = 200;
const NEAR_ALTITUDE_HIGH_M = 5000;

/**
 * `adaptShadowDistance` altitude behaviour, metres.
 *
 * The cascades are sized for standing on the ground, so flying covers them
 * in a shrinking fraction of the view — growing the base distance with
 * altitude buys back coverage the same way the depth partitions already do.
 * Past `FADE_START` self-shadow stops being something the player can resolve
 * anyway (terrain detail itself is thinning out at the same altitudes), so it
 * fades to nothing by `FADE_END` rather than paying for cascades nobody can
 * see the benefit of.
 */
const SHADOW_ALTITUDE_GROWTH_START_M = 200;
const SHADOW_ALTITUDE_GROWTH_FACTOR = 6;
const SHADOW_DISTANCE_MAX_MULTIPLIER = 25;
const SHADOW_FADE_START_M = 4000;
const SHADOW_FADE_END_M = 20000;

/** `worldNear` partition bounds per altitude bracket above, metres. */
const NEAR_NEAR_AT_LOW_ALT_M = 0.2;
const NEAR_NEAR_AT_MID_ALT_M = 0.5;
const NEAR_NEAR_AT_HIGH_ALT_M = 4;
const NEAR_FAR_AT_LOW_ALT_M = 400;
const NEAR_FAR_AT_MID_ALT_M = 2500;
const NEAR_FAR_AT_HIGH_ALT_M = 20000;

/**
 * `worldMid` partition. Its near plane sits inside `worldNear`'s far plane
 * (the overlap is deliberate — see the class doc comment); its far plane
 * grows with altitude but never below `MID_FAR_MIN_M` and never past
 * `MID_FAR_PLANET_RADIUS_FACTOR` planet radii, since there's no terrain left
 * to show beyond that.
 */
const MID_NEAR_OVERLAP_FRACTION = 0.75;
const MID_FAR_MIN_M = 60_000;
const MID_FAR_ALTITUDE_FACTOR = 12;
const MID_FAR_PLANET_RADIUS_FACTOR = 2;

/**
 * `worldFar` partition. Same overlap idea as `worldMid` above; its far plane
 * has to reach at least `FAR_FAR_PLANET_RADIUS_MIN_FACTOR` planet radii out
 * (so the planet's own limb is visible from outside it) and grows further
 * with altitude for a high-orbit view of distant terrain.
 */
const FAR_NEAR_OVERLAP_FRACTION = 0.7;
const FAR_FAR_PLANET_RADIUS_MIN_FACTOR = 40;
const FAR_FAR_ALTITUDE_FACTOR = 40;
const FAR_FAR_PLANET_RADIUS_OFFSET_FACTOR = 4;

/**
 * Safety margin applied to the nearest drawn point of the sky shell before
 * `worldFar.near` is clamped against it — see `adaptDepthRanges`. The shell is
 * drawn *only* by the far pass (see `RENDER_LAYER.sky`), so anything the far
 * camera's near plane clips is not merely mis-sorted, it is simply not drawn:
 * black sky with a hard edge along the cone where the shell crosses that plane.
 * Reported as "at altitude, looking up, the atmosphere is cut off hard".
 */
const SKY_SHELL_NEAR_MARGIN = 0.9;

/**
 * Highest `worldFar.near` that still leaves the whole sky shell drawable,
 * metres. All radii are distances from the body's centre.
 *
 * Two separate geometric facts, and getting either wrong reintroduces the
 * hard-edged black cut-off this exists to prevent:
 *
 *   - The nearest point of the shell is the zenith backface from inside it and
 *     the silhouette (where the near and far faces meet) from outside, hence
 *     the branch. The outside case is factored as `(r-s)(r+s)` rather than
 *     `r*r - s*s`: at planetary radii those are two near-equal 1e13s with no
 *     precision left after the subtraction.
 *   - That is a straight-line distance, but a near plane is a PLANE. A
 *     fragment is clipped on its view-space depth, which for a ray `a` off the
 *     camera axis is only `distance * cos(a)` — so the near plane reaches
 *     `near / cos(a)` along that ray, and the frustum corner is the worst
 *     case. At the default 62-degree vertical FOV and 16:9 the corner is
 *     50.8 degrees off axis, cos 0.632: it clips 1.58x further out than the
 *     centre of the frame does. Comparing the straight-line distance against
 *     `near` directly leaves the shell clipped in the corners whenever the
 *     zenith sits near one rather than at the centre — reported as "in flight,
 *     looking nearly straight up, the blue sky ends on a hard diagonal".
 *
 * `shellDistance` is already the minimum over every direction and
 * `frustumCornerCos` the minimum over every ray in the frustum, so their
 * product is a true lower bound on the shell's view-space depth anywhere on
 * screen — no attitude can defeat it.
 */
export function skyShellNearLimit(
  cameraRadius: number,
  skyShellRadius: number,
  fovDeg: number,
  aspect: number,
): number {
  const shellDistance =
    cameraRadius < skyShellRadius
      ? skyShellRadius - cameraRadius
      : Math.sqrt(Math.max(0, (cameraRadius - skyShellRadius) * (cameraRadius + skyShellRadius)));
  const tanV = Math.tan((fovDeg * Math.PI) / 360);
  const tanH = tanV * aspect;
  const frustumCornerCos = 1 / Math.sqrt(1 + tanV * tanV + tanH * tanH);
  return shellDistance * frustumCornerCos * SKY_SHELL_NEAR_MARGIN;
}

/**
 * Shadow cascade camera geometry (see `updateCascadeSplits`/`updateCascadeCameras`).
 */
const SHADOW_CAM_NEAR_M = 0.1;
/** Reach along the light axis, as a multiple of the cascade's own half-width plus a flat margin — generous on purpose: this only has to contain terrain height variation near the camera, not track a real distance. */
const SHADOW_CAM_FAR_DEPTH_FACTOR = 4;
const SHADOW_CAM_FAR_MARGIN_M = 50;
/** How far back along the light direction the cascade camera sits, same shape as its far-plane reach above. */
const SHADOW_CAM_STANDOFF_DEPTH_FACTOR = 2;
const SHADOW_CAM_STANDOFF_MARGIN_M = 25;
/** `lookAt` degenerates when the view direction is parallel to `up`; swap the cascade's up axis when the light direction gets this close to vertical. */
const UP_AXIS_SINGULARITY_THRESHOLD = 0.999;

/** `setRenderScale` bounds — below MIN the image gets too blocky to be worth the saved cost; above MAX there's no visible benefit on top of the quality profile's own pixel-ratio cap. */
const RENDER_SCALE_MIN = 0.5;
const RENDER_SCALE_MAX = 2;

/** Default vertical FOV, degrees; adjustable at runtime via `setFov`. */
const DEFAULT_FOV_DEG = 62;

/** Baked galaxy background resolution, px per side — halved on the low quality tier since it's a soft, low-frequency backdrop that doesn't need to resolve fine detail. */
const GALAXY_TEXTURE_SIZE_LOW = 512;
const GALAXY_TEXTURE_SIZE_HIGH = 1024;

/**
 * Arbitrary distance the star's `DirectionalLight` is placed at, metres. Only
 * matters as "far enough that (position - target) is a stable direction
 * vector" — a directional light's actual intensity/shading doesn't depend on
 * distance at all.
 */
const STAR_LIGHT_DISTANCE_M = 1000;

/** Floor under `skyLight`'s intensity so ambient light never goes fully to zero, even with no sun and no planetshine. Shared by the two terms that drive it (direct sky strength and reflected planetshine — see `setStarLight`/`setPlanetshine`) so neither one can push the other's contribution below this floor. */
const SKY_LIGHT_BASE_INTENSITY = 0.05;
/** How strongly the local atmosphere's `skyStrength` (density x sun elevation) drives ambient fill light — see `setStarLight`. */
const SKY_LIGHT_STRENGTH_SCALE = 0.45;
/** How strongly reflected planetshine drives ambient fill light — see `setPlanetshine`. */
const PLANETSHINE_INTENSITY_SCALE = 0.6;

export class LayeredRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly deepSpaceScene = new THREE.Scene();
  readonly astronomicalScene = new THREE.Scene();
  readonly worldScene = new THREE.Scene();
  readonly firstPersonScene = new THREE.Scene();
  /**
   * Lens effects, drawn after every other pass.
   *
   * Glare is not in the world, it is in the optics — it forms after the image
   * has been focused, so nothing in the scene can be in front of it. Held in
   * the astronomical pass it was drawn second of six and the three world passes
   * then painted over it, which is why the star's glare disappeared behind a
   * planet it was nowhere near.
   */
  readonly overlayScene = new THREE.Scene();

  readonly deepSpaceCamera: THREE.PerspectiveCamera;
  readonly astronomicalCamera: THREE.PerspectiveCamera;
  readonly worldFarCamera: THREE.PerspectiveCamera;
  readonly worldMidCamera: THREE.PerspectiveCamera;
  readonly worldNearCamera: THREE.PerspectiveCamera;
  readonly firstPersonCamera: THREE.PerspectiveCamera;

  /** Vertical field of view, degrees. */
  fov = DEFAULT_FOV_DEG;

  /**
   * User-controlled resolution multiplier, independent of the quality
   * profile's `pixelRatio` (which is a device-pixel-ratio cap, not a scale
   * the player asked for). Applied on top of it.
   */
  private renderScale = 1;

  /** Directional light representing the star, for standard-material objects. */
  readonly starLight: THREE.DirectionalLight;
  /** Weak hemisphere term standing in for skylight and planetshine. */
  readonly skyLight: THREE.HemisphereLight;

  /**
   * Terrain self-shadow cascades. Empty when disabled (low quality, or the
   * player's toggle). Rendered once per frame regardless of how many world
   * passes follow — see `renderShadowCascades`.
   */
  private readonly cascades: ShadowCascade[] = [];
  /**
   * Depth-only override for the cascade passes. `colorWrite: false` skips
   * fragment work entirely; the thing the cascade actually captures is the
   * hardware depth buffer, via each target's attached `DepthTexture`.
   *
   * `polygonOffset` nudges the caster's captured depth away from its own
   * surface, so a fragment of terrain never tests as shadowed by *itself*
   * where the map quantises the very surface it samples. This is the classic
   * companion to a metered depth bias (see the `uShadowRanges` fix in
   * TerrainMaterial) for self-shadow acne; together they keep the terrain
   * from flickering between lit and shadowed at pixel level.
   */
  private readonly shadowDepthMaterial = new THREE.MeshBasicMaterial({
    colorWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 4,
  });
  private readonly cascadeLightDir = new THREE.Vector3();
  private readonly cascadeRightAxis = new THREE.Vector3();
  private readonly cascadeUpAxis = new THREE.Vector3();
  private terrainShadowEnabled = true;
  /** `quality.shadowDistance` as last set by `setupCascades`; the base `adaptShadowDistance` scales from. */
  private baseShadowDistance = 0;
  /** Self-shadow strength last computed by `adaptShadowDistance`, 0..1. Read by `Game.render` and handed to `TerrainRenderer.setShadowFade`. */
  shadowFade = 1;

  /** Retained so `setRenderScale` can recompute the pixel ratio on its own. */
  private lastQuality: QualitySettings;
  private lastCaps: DeviceCapabilities;

  /**
   * Screen-space reflection capture for the ocean. Created when the quality
   * profile enables it (high/ultra); null on lower tiers so the extra world
   * render and its memory simply do not exist there.
   */
  private ssr: ScreenSpaceReflection | null = null;
  /**
   * Objects hidden while the SSR capture renders (the ocean's own meshes), so
   * a reflected ray cannot re-hit the water it reflects from.
   */
  private readonly ssrExcluded = new Set<THREE.Object3D>();
  /** Camera altitude, stashed by `adaptDepthRanges` for the SSR far plane. */
  private ssrAltitude = 0;

  private readonly partitions: Record<PassName, DepthPartition> = {
    deepSpace: { name: 'deepSpace', near: DEEP_SPACE_NEAR_M, far: DEEP_SPACE_FAR_M },
    astronomical: { name: 'astronomical', near: ASTRONOMICAL_NEAR_M, far: ASTRONOMICAL_FAR_M },
    worldFar: { name: 'worldFar', near: WORLD_FAR_NEAR_M, far: WORLD_FAR_FAR_M },
    worldMid: { name: 'worldMid', near: WORLD_MID_NEAR_M, far: WORLD_MID_FAR_M },
    worldNear: { name: 'worldNear', near: WORLD_NEAR_NEAR_M, far: WORLD_NEAR_FAR_M },
    firstPerson: { name: 'firstPerson', near: FIRST_PERSON_NEAR_M, far: FIRST_PERSON_FAR_M },
  };

  /** Draw statistics for the last frame. */
  private readonly canvas: HTMLCanvasElement;

  private readonly handleContextLost = (event: Event): void => {
    event.preventDefault();
    console.warn('[LayeredRenderer] WebGL context lost; waiting for restore');
  };

  private readonly handleContextRestored = (): void => {
    console.warn('[LayeredRenderer] WebGL context restored; regenerating terrain geometry');
    this.onContextRestored?.();
  };

  readonly stats = {
    drawCalls: 0,
    triangles: 0,
    programs: 0,
    geometries: 0,
    textures: 0,
    frameMs: 0,
  };

  /**
   * Called after the GPU hands the context back following a loss.
   *
   * This exists because terrain geometry no longer keeps a CPU copy it could
   * be re-uploaded from (see `releaseAfterUpload` in `TerrainRenderer`).
   * Three restores everything it still owns the source data for — textures,
   * render targets, shader programs — but a `BufferAttribute` whose array has
   * been dropped is gone, and the owner has to regenerate it. `Game` wires
   * this to `PlanetSurface.invalidateAll`, which re-requests every resident
   * chunk through the ordinary streaming path.
   */
  onContextRestored: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement, quality: QualitySettings, caps: DeviceCapabilities) {
    // Without `preventDefault` the browser never fires `webglcontextrestored`
    // at all, and the canvas stays dead for the rest of the session.
    canvas.addEventListener('webglcontextlost', this.handleContextLost);
    canvas.addEventListener('webglcontextrestored', this.handleContextRestored);
    this.canvas = canvas;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: quality.level !== 'low',
      powerPreference: 'high-performance',
      alpha: false,
      stencil: false,
      depth: true,
      // Deliberately NOT enabled: a logarithmic depth buffer would let one
      // camera span every scale, but it costs a per-fragment depth write on
      // WebGL2 (disabling early-Z), complicates every custom shader, and
      // interacts badly with the transparent atmosphere shell. Depth
      // partitioning achieves the same result with none of those costs.
      logarithmicDepthBuffer: false,
      // Off by default: preserving the drawing buffer costs a full-frame copy
      // every frame. Enabled only for automated visual checks, where the
      // screenshot is taken outside the render loop and would otherwise capture
      // an already-swapped, undefined buffer.
      preserveDrawingBuffer: quality.captureMode === true,
    });

    this.lastQuality = quality;
    this.lastCaps = caps;
    this.renderer.setPixelRatio(Math.min(quality.pixelRatio, caps.devicePixelRatio) * this.renderScale);
    this.renderer.autoClear = false;
    // Three resets render statistics at the start of every `render()` call.
    // With six passes per frame that would leave the counters reporting only
    // the last pass, so the diagnostics would show near-zero draw calls no
    // matter how much was actually drawn. Reset once per frame instead.
    this.renderer.info.autoReset = false;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = quality.shadowMapSize > 0;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.setupCascades(quality);

    const aspect = canvas.clientWidth / Math.max(1, canvas.clientHeight);
    const make = (p: DepthPartition): THREE.PerspectiveCamera => {
      const cam = new THREE.PerspectiveCamera(this.fov, aspect, p.near, p.far);
      cam.matrixAutoUpdate = true;
      return cam;
    };

    this.deepSpaceCamera = make(this.partitions.deepSpace);
    this.astronomicalCamera = make(this.partitions.astronomical);
    this.worldFarCamera = make(this.partitions.worldFar);
    this.worldMidCamera = make(this.partitions.worldMid);
    this.worldNearCamera = make(this.partitions.worldNear);
    this.firstPersonCamera = make(this.partitions.firstPerson);

    // Only the far pass draws sky shells; see RENDER_LAYER.
    this.worldFarCamera.layers.enable(RENDER_LAYER.sky);
    this.worldFarCamera.layers.enable(RENDER_LAYER.clouds);
    this.worldMidCamera.layers.enable(RENDER_LAYER.clouds);
    this.worldNearCamera.layers.enable(RENDER_LAYER.clouds);
    this.updateCloudPassRanges();

    this.worldScene.background = null;
    this.astronomicalScene.background = null;

    // Terrain, atmosphere and ocean are drawn with custom shaders that light
    // themselves from the star direction. The vessel hull uses Three's standard
    // material, so it needs an actual light: one directional light representing
    // the star, plus a weak hemisphere term standing in for skylight and
    // planetshine. There is no arbitrary fill light; on an airless night side
    // the hull really is close to black.
    this.starLight = new THREE.DirectionalLight(0xfff3e6, 3);
    this.starLight.position.set(0, 0, 1);
    this.worldScene.add(this.starLight);
    this.worldScene.add(this.starLight.target);

    this.skyLight = new THREE.HemisphereLight(0x2a3c52, 0x1a1712, 0.25);
    this.worldScene.add(this.skyLight);

    this.firstPersonScene.add(new THREE.HemisphereLight(0x3a4a60, 0x201d18, 0.6));

    // Baked once. See GalaxyBackground: the band, its dust and the nebulae are
    // all low-frequency and entirely static, so they cost one cube lookup per
    // pixel instead of a per-frame fbm.
    try {
      this.galaxy = new GalaxyBackground(
        this.renderer,
        quality.level === 'low' ? GALAXY_TEXTURE_SIZE_LOW : GALAXY_TEXTURE_SIZE_HIGH,
      );
      this.deepSpaceScene.background = this.galaxy.texture;
      this.deepSpaceScene.backgroundIntensity = 1;
    } catch (err) {
      // A background is not worth failing to start over.
      console.warn('[LayeredRenderer] galaxy bake failed; falling back to point stars only', err);
    }

    this.configureSsr(quality.ssr, quality.ssrScale);
  }

  private galaxy: GalaxyBackground | null = null;

  /**
   * Creates or tears down the SSR capture to match the quality profile. Cheap
   * to call on every quality change: it only allocates the first time SSR is
   * turned on and only disposes if it is being switched off.
   */
  private configureSsr(enabled: boolean, scale: number): void {
    if (enabled && !this.ssr) {
      const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
      this.ssr = new ScreenSpaceReflection(this.renderer, scale, this.fov);
      this.ssr.camera.aspect = size.x / Math.max(1, size.y);
      this.ssr.camera.updateProjectionMatrix();
    } else if (!enabled && this.ssr) {
      this.ssr.dispose();
      this.ssr = null;
    }
  }

  /** Registers an object to hide during the SSR capture (e.g. a body's ocean). */
  addSsrExclude(obj: THREE.Object3D): void {
    this.ssrExcluded.add(obj);
  }

  removeSsrExclude(obj: THREE.Object3D): void {
    this.ssrExcluded.delete(obj);
  }

  /** The live SSR capture, or null when the quality profile disables it. */
  get screenSpaceReflection(): ScreenSpaceReflection | null {
    return this.ssr;
  }

  get cameras(): THREE.PerspectiveCamera[] {
    return [
      this.deepSpaceCamera,
      this.astronomicalCamera,
      this.worldFarCamera,
      this.worldMidCamera,
      this.worldNearCamera,
      this.firstPersonCamera,
      ...(this.ssr ? [this.ssr.camera] : []),
    ];
  }

  /** Applies the shared view orientation to every camera. */
  setViewOrientation(orientation: Quat): void {
    for (const cam of this.cameras) {
      cam.quaternion.set(orientation.x, orientation.y, orientation.z, orientation.w);
      cam.position.set(0, 0, 0);
      cam.updateMatrixWorld(true);
    }
  }

  setFov(fovDegrees: number): void {
    this.fov = fovDegrees;
    for (const cam of this.cameras) {
      cam.fov = fovDegrees;
      cam.updateProjectionMatrix();
    }
  }

  /**
   * Adapts the world partitions to the current altitude.
   *
   * On the ground the near partition needs to start at a few centimetres; from
   * orbit it can start kilometres away, which buys back a great deal of depth
   * precision for the terrain that is actually visible.
   */
  /**
   * Retunes the three world partitions for the viewer's current altitude.
   *
   * `cameraRadius`/`skyShellRadius` (both distances from the body's centre,
   * metres; pass 0 for a body with no atmosphere) exist only to keep the sky
   * shell out of the far camera's near plane. Everything else in the world
   * scene spans a depth range, so a partition boundary merely decides which
   * pass draws it; the shell is confined to one pass and surrounds the camera,
   * so its nearest point has to stay drawable in that pass or that part of the
   * sky disappears outright.
   */
  adaptDepthRanges(
    altitude: number,
    planetRadius: number,
    cameraRadius = 0,
    skyShellRadius = 0,
  ): void {
    const a = Math.max(0, altitude);

    const near = this.partitions.worldNear;
    near.near =
      a < NEAR_ALTITUDE_LOW_M
        ? NEAR_NEAR_AT_LOW_ALT_M
        : a < NEAR_ALTITUDE_HIGH_M
          ? NEAR_NEAR_AT_MID_ALT_M
          : NEAR_NEAR_AT_HIGH_ALT_M;
    near.far =
      a < NEAR_ALTITUDE_LOW_M
        ? NEAR_FAR_AT_LOW_ALT_M
        : a < NEAR_ALTITUDE_HIGH_M
          ? NEAR_FAR_AT_MID_ALT_M
          : NEAR_FAR_AT_HIGH_ALT_M;

    const mid = this.partitions.worldMid;
    mid.near = near.far * MID_NEAR_OVERLAP_FRACTION;
    mid.far = Math.max(
      MID_FAR_MIN_M,
      Math.min(planetRadius * MID_FAR_PLANET_RADIUS_FACTOR, a * MID_FAR_ALTITUDE_FACTOR + MID_FAR_MIN_M),
    );

    const far = this.partitions.worldFar;
    far.near = mid.far * FAR_NEAR_OVERLAP_FRACTION;
    if (skyShellRadius > 0 && cameraRadius > 0) {
      // See `skyShellNearLimit` for why the shell needs its own bound at all
      // and why it carries a frustum-corner cosine. Floored at the mid
      // partition's own near plane: below that the far pass's near/far ratio
      // starts costing real depth precision, and by then the camera is within
      // a kilometre or two of the top of the atmosphere, where what is being
      // clipped is already black.
      far.near = Math.max(
        mid.near,
        Math.min(far.near, skyShellNearLimit(cameraRadius, skyShellRadius, this.fov, this.worldFarCamera.aspect)),
      );
    }
    far.far = Math.max(
      planetRadius * FAR_FAR_PLANET_RADIUS_MIN_FACTOR,
      a * FAR_FAR_ALTITUDE_FACTOR + planetRadius * FAR_FAR_PLANET_RADIUS_OFFSET_FACTOR,
    );

    this.applyPartition(this.worldNearCamera, near);
    this.applyPartition(this.worldMidCamera, mid);
    this.applyPartition(this.worldFarCamera, far);
    this.updateCloudPassRanges();

    this.ssrAltitude = a;
  }

  private applyPartition(camera: THREE.PerspectiveCamera, p: DepthPartition): void {
    if (camera.near === p.near && camera.far === p.far) return;
    camera.near = p.near;
    camera.far = p.far;
    camera.updateProjectionMatrix();
  }

  /**
   * Transparent cloud shells are visible in the deliberate overlap between
   * world depth partitions. Opaque terrain is protected by depth, but alpha
   * blending the same shell twice creates a bright horizontal band. These
   * ownership intervals retain the camera overlap for clipping precision while
   * letting CloudLayer accept each proxy fragment in exactly one pass.
   */
  private updateCloudPassRanges(): void {
    this.worldNearCamera.userData.cloudPassMin = 0;
    this.worldNearCamera.userData.cloudPassMax = this.partitions.worldNear.far;
    this.worldMidCamera.userData.cloudPassMin = this.partitions.worldNear.far;
    this.worldMidCamera.userData.cloudPassMax = this.partitions.worldMid.far;
    this.worldFarCamera.userData.cloudPassMin = this.partitions.worldMid.far;
    this.worldFarCamera.userData.cloudPassMax = 1e30;

    // The ocean is also alpha blended. Give it the same non-overlapping
    // ownership intervals while retaining the cameras' geometric overlap, or
    // sun glitter is composited two or three times into white horizon bands.
    this.worldNearCamera.userData.transparentPassMin = 0;
    this.worldNearCamera.userData.transparentPassMax = this.partitions.worldNear.far;
    this.worldMidCamera.userData.transparentPassMin = this.partitions.worldNear.far;
    this.worldMidCamera.userData.transparentPassMax = this.partitions.worldMid.far;
    this.worldFarCamera.userData.transparentPassMin = this.partitions.worldMid.far;
    this.worldFarCamera.userData.transparentPassMax = 1e30;
  }

  getPartition(name: PassName): DepthPartition {
    return this.partitions[name];
  }

  resize(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
    const aspect = width / Math.max(1, height);
    for (const cam of this.cameras) {
      cam.aspect = aspect;
      cam.updateProjectionMatrix();
    }
    this.ssr?.resize(this.renderer);
  }

  setQuality(quality: QualitySettings, caps: DeviceCapabilities): void {
    this.lastQuality = quality;
    this.lastCaps = caps;
    this.renderer.setPixelRatio(Math.min(quality.pixelRatio, caps.devicePixelRatio) * this.renderScale);
    this.renderer.shadowMap.enabled = quality.shadowMapSize > 0;
    this.setupCascades(quality);
    this.configureSsr(quality.ssr, quality.ssrScale);
  }

  /**
   * User-facing resolution scale, independent of the quality preset. Applied
   * on top of the preset's device-pixel-ratio cap rather than replacing it.
   */
  setRenderScale(scale: number): void {
    this.renderScale = clamp(scale, RENDER_SCALE_MIN, RENDER_SCALE_MAX);
    this.renderer.setPixelRatio(Math.min(this.lastQuality.pixelRatio, this.lastCaps.devicePixelRatio) * this.renderScale);
  }

  /**
   * Player-facing toggle, independent of quality. Off recovers the cost of
   * the cascade passes entirely rather than just hiding the result.
   */
  setTerrainShadowEnabled(enabled: boolean): void {
    this.terrainShadowEnabled = enabled;
    this.setupCascades(this.lastQuality);
  }

  /**
   * (Re)builds the cascade render targets for the given quality profile.
   * Cheap to call whenever quality changes: it only reallocates when the
   * cascade count or resolution actually differ from what is already built.
   */
  private setupCascades(quality: QualitySettings): void {
    const count =
      this.terrainShadowEnabled && quality.shadowMapSize > 0
        ? Math.min(quality.shadowCascades, MAX_CASCADES)
        : 0;
    const size = quality.shadowMapSize;

    if (this.cascades.length === count && this.cascades.every((c) => c.target.width === size)) {
      this.updateCascadeSplits(quality);
      return;
    }

    for (const cascade of this.cascades) {
      cascade.target.dispose();
      cascade.target.depthTexture?.dispose();
    }
    this.cascades.length = 0;

    for (let i = 0; i < count; i++) {
      const depthTexture = new THREE.DepthTexture(size, size);
      depthTexture.minFilter = THREE.NearestFilter;
      depthTexture.magFilter = THREE.NearestFilter;
      const target = new THREE.WebGLRenderTarget(size, size, { depthTexture, depthBuffer: true });
      // Placeholder bounds — every field here is overwritten by the
      // `updateCascadeSplits` call at the end of this method.
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, SHADOW_CAM_NEAR_M, 10);
      this.cascades.push({ camera, target, splitDistance: 0, matrix: new THREE.Matrix4() });
    }
    this.updateCascadeSplits(quality);
  }

  private updateCascadeSplits(quality: QualitySettings): void {
    this.baseShadowDistance = quality.shadowDistance;
    this.applyCascadeDistance(quality.shadowDistance);
  }

  private applyCascadeDistance(distance: number): void {
    for (let i = 0; i < this.cascades.length; i++) {
      const splitDistance = distance * CASCADE_SPLIT_FRACTIONS[i];
      const cascade = this.cascades[i];
      cascade.splitDistance = splitDistance;
      const cam = cascade.camera;
      cam.left = -splitDistance;
      cam.right = splitDistance;
      cam.top = splitDistance;
      cam.bottom = -splitDistance;
      cam.near = SHADOW_CAM_NEAR_M;
      // Generous along the light axis: this only has to contain terrain
      // height variation near the camera, not track a real distance.
      cam.far = splitDistance * SHADOW_CAM_FAR_DEPTH_FACTOR + SHADOW_CAM_FAR_MARGIN_M;
      cam.updateProjectionMatrix();
    }
  }

  /**
   * Scales the cascade distances with altitude and fades their effect out at
   * high altitude. Call once per frame, alongside `adaptDepthRanges`.
   *
   * Growth buys back coverage the same way the depth partitions do: the
   * cascades are sized for standing on the ground, so without this a climb
   * shrinks them to a fraction of the visible terrain almost immediately.
   * The fade exists because growing them without bound eventually costs real
   * frame time rendering cascades whose ground detail nobody can resolve
   * from that high up anyway — `TerrainRenderer.setShadowFade` blends the
   * self-shadow term back toward "fully lit" rather than the cascades
   * visibly switching off, which would pop.
   */
  adaptShadowDistance(altitude: number): void {
    if (this.cascades.length === 0) return;
    const a = Math.max(0, altitude);
    const growth = 1 + (Math.max(0, a - SHADOW_ALTITUDE_GROWTH_START_M) / SHADOW_ALTITUDE_GROWTH_START_M) * SHADOW_ALTITUDE_GROWTH_FACTOR;
    const multiplier = Math.min(SHADOW_DISTANCE_MAX_MULTIPLIER, growth);
    this.applyCascadeDistance(this.baseShadowDistance * multiplier);

    this.shadowFade = 1 - THREE.MathUtils.smoothstep(a, SHADOW_FADE_START_M, SHADOW_FADE_END_M);
  }

  /**
   * Points every cascade at the current star direction. Everything in
   * `worldScene` is camera-relative with the camera fixed at the render
   * origin (see `ARCHITECTURE.md` §1), so a fixed-size box centred on the
   * origin is correct regardless of where the player actually is in the
   * universe — nothing here has to track real planetary coordinates.
   *
   * That same re-centring is what makes the shadow map shimmer without the
   * texel snap below. The cascade camera always looks at the render origin,
   * which *is* the true camera position — a value that moves continuously in
   * float64 (real flight, or even just landing-gear/suspension noise while
   * "parked") and gets re-zeroed every frame. So although the cascade
   * camera's render-relative transform looks stable frame to frame, its
   * effective world-space aim point tracks the true camera exactly, and the
   * shadow map's texel grid slides by whatever sub-texel amount the camera
   * moved. Terrain (and, since this session, the ocean) then samples that
   * grid at a slightly different sub-texel offset every frame, which PCF
   * turns into per-pixel flicker at every slope/shadow boundary — reported
   * as constant, high-frequency "jiggling" that gets worse the closer the
   * camera is (a shadow texel covers more of the screen up close) and
   * affects terrain and ocean identically because both sample the same
   * cascades through the same bias/PCF code shape.
   *
   * The fix is the standard CSM texel snap: round the true camera position,
   * projected onto this cascade's own right/up axes, to the nearest whole
   * texel (in world metres), and shift the camera by the (tiny, sub-texel,
   * float32-safe) residual. That pins the shadow map's grid to a lattice
   * fixed in true world space rather than to wherever the camera happens to
   * sit within a texel this frame, so a stationary — or slowly moving —
   * scene now samples the same texels every frame instead of drifting.
   */
  private updateCascadeCameras(cameraPosition: Vec3): void {
    if (this.cascades.length === 0) return;
    const dir = this.cascadeLightDir.copy(this.starLight.position).normalize();
    for (const cascade of this.cascades) {
      const cam = cascade.camera;
      const distance = cascade.splitDistance * SHADOW_CAM_STANDOFF_DEPTH_FACTOR + SHADOW_CAM_STANDOFF_MARGIN_M;
      cam.position.copy(dir).multiplyScalar(distance);
      // `lookAt` degenerates when the view direction is parallel to `up`;
      // swap to a different up axis in that case rather than let the
      // cascade's basis collapse.
      const nearVertical = Math.abs(dir.y) > UP_AXIS_SINGULARITY_THRESHOLD;
      cam.up.set(0, nearVertical ? 0 : 1, nearVertical ? 1 : 0);
      cam.lookAt(0, 0, 0);
      cam.updateMatrixWorld(true);

      // Texel snap. Render-space directions equal true-world directions here
      // (the render frame is a pure translation of the inertial frame, never
      // rotated — see ARCHITECTURE.md §1), so the cascade's own right/up
      // axes can be dotted directly against the true camera position without
      // any frame conversion.
      const right = this.cascadeRightAxis.setFromMatrixColumn(cam.matrixWorld, 0);
      const up = this.cascadeUpAxis.setFromMatrixColumn(cam.matrixWorld, 1);
      const texelSize = (cam.right - cam.left) / cascade.target.width;
      if (texelSize > 0) {
        const offsetRight = cameraPosition.x * right.x + cameraPosition.y * right.y + cameraPosition.z * right.z;
        const offsetUp = cameraPosition.x * up.x + cameraPosition.y * up.y + cameraPosition.z * up.z;
        const residualRight = Math.round(offsetRight / texelSize) * texelSize - offsetRight;
        const residualUp = Math.round(offsetUp / texelSize) * texelSize - offsetUp;
        cam.position.addScaledVector(right, residualRight).addScaledVector(up, residualUp);
        cam.updateMatrixWorld(true);
      }

      cascade.matrix.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    }
  }

  /**
   * Renders the terrain-only depth-only pass for every cascade. Runs once
   * per frame, not once per world pass: the three world cameras share this
   * frame's cascades unchanged, since only the camera (not the light or the
   * terrain) differs between those passes.
   */
  private renderShadowCascades(): void {
    if (this.cascades.length === 0) return;
    const r = this.renderer;
    const previousOverride = this.worldScene.overrideMaterial;
    this.worldScene.overrideMaterial = this.shadowDepthMaterial;
    for (const cascade of this.cascades) {
      cascade.camera.layers.set(RENDER_LAYER.terrainShadowCaster);
      r.setRenderTarget(cascade.target);
      r.clear(true, true, false);
      r.render(this.worldScene, cascade.camera);
    }
    r.setRenderTarget(null);
    this.worldScene.overrideMaterial = previousOverride;
  }

  /**
   * Points the cascades at the current star direction and renders their
   * terrain-only depth pass. Must run after `setStarLight` for this frame
   * and before anything reads `getShadowCascades()` — Three's own `render()`
   * happens too late for that, so this is a separate step `Game.render()`
   * calls explicitly rather than something folded into `render()` below.
   *
   * @param cameraPosition true camera position this frame (the same 64-bit
   * value everything else in `Game.render()` is made relative to), needed
   * only for the texel snap in `updateCascadeCameras` — see its own comment.
   */
  updateShadowCascades(cameraPosition: Vec3): void {
    this.updateCascadeCameras(cameraPosition);
    this.renderShadowCascades();
  }

  /** Read-only view for `TerrainMaterial`'s uniforms; empty when disabled. */
  getShadowCascades(): readonly ShadowCascade[] {
    return this.cascades;
  }

  /**
   * Dims the baked galaxy the same way the point stars are dimmed, so the
   * background and the stars in front of it fade together rather than leaving
   * a visible band hanging in a bright daytime sky.
   */
  setBackgroundIntensity(intensity: number): void {
    this.deepSpaceScene.backgroundIntensity = Math.max(0, intensity);
  }

  /** Debug: removes the baked galaxy without discarding the bake. */
  setGalaxyVisible(visible: boolean): void {
    this.deepSpaceScene.background = visible ? (this.galaxy?.texture ?? null) : null;
  }

  setExposure(exposure: number): void {
    this.renderer.toneMappingExposure = exposure;
  }

  /**
   * Points the star light. `direction` is the unit vector toward the star in
   * the camera's frame; `intensity` is the local irradiance already normalised.
   */
  setStarLight(direction: THREE.Vector3, color: THREE.Color, intensity: number, skyStrength: number): void {
    this.starLight.position.copy(direction).multiplyScalar(STAR_LIGHT_DISTANCE_M);
    this.starLight.target.position.set(0, 0, 0);
    this.starLight.color.copy(color);
    this.starLight.intensity = intensity;
    this.skyLight.intensity = SKY_LIGHT_BASE_INTENSITY + skyStrength * SKY_LIGHT_STRENGTH_SCALE;
  }

  /**
   * Adds reflected neighbour light to the hull's fill.
   *
   * Folded into the hemisphere light's sky colour rather than given its own
   * directional light: the hull is a small object under a very large area
   * source, so the direction buys almost nothing and a second shadow-casting
   * light would cost a great deal.
   */
  setPlanetshine(color: THREE.Color, intensity: number): void {
    this.skyLight.color.copy(color);
    this.skyLight.intensity = Math.max(
      this.skyLight.intensity,
      SKY_LIGHT_BASE_INTENSITY + intensity * PLANETSHINE_INTENSITY_SCALE,
    );
  }

  /** Renders all six passes. */
  render(): void {
    const start = performance.now();
    const r = this.renderer;

    r.info.reset();
    r.clear(true, true, true);

    // Screen-space reflection capture for the ocean. Rendered before the
    // world passes that draw the ocean, so the water reads this frame's
    // reflection when it is drawn. Cost is gated by the quality profile
    // (high/ultra) and the capture renders at reduced resolution.
    if (this.ssr) {
      this.ssr.capture(this.renderer, this.worldScene, this.ssrExcluded, this.ssrAltitude);
    }

    // 1. Deep space. No depth interaction with anything.
    r.render(this.deepSpaceScene, this.deepSpaceCamera);

    // Terrain self-shadow cascades were already rendered this frame by
    // `updateShadowCascades()` (called from `Game.render()`, ahead of the
    // terrain-lighting code that reads them back) — not here, since by the
    // time this method runs it is too late for that data to reach the
    // terrain uniforms for the current frame.


    // 2. Distant celestial bodies at compressed distances.
    r.clearDepth();
    r.render(this.astronomicalScene, this.astronomicalCamera);

    // 3-5. The world, three depth partitions, far to near.
    r.clearDepth();
    r.render(this.worldScene, this.worldFarCamera);
    r.clearDepth();
    r.render(this.worldScene, this.worldMidCamera);
    r.clearDepth();
     r.render(this.worldScene, this.worldNearCamera);

    // 6. Cockpit and first-person geometry.
    r.clearDepth();
    r.render(this.firstPersonScene, this.firstPersonCamera);

    // 7. Lens effects, over everything. Uses the astronomical camera because
    // the glare is positioned in that pass's compressed coordinates; only the
    // draw order changes, not the projection.
    r.clearDepth();
    r.render(this.overlayScene, this.astronomicalCamera);

    this.stats.drawCalls = r.info.render.calls;
    this.stats.triangles = r.info.render.triangles;
    this.stats.programs = r.info.programs?.length ?? 0;
    this.stats.geometries = r.info.memory.geometries;
    this.stats.textures = r.info.memory.textures;
    this.stats.frameMs = performance.now() - start;
  }

  dispose(): void {
    this.canvas.removeEventListener('webglcontextlost', this.handleContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.handleContextRestored);
    this.galaxy?.dispose();
    this.ssr?.dispose();
    for (const cascade of this.cascades) {
      cascade.target.dispose();
      cascade.target.depthTexture?.dispose();
    }
    this.shadowDepthMaterial.dispose();
    this.renderer.dispose();
  }
}
